<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate :disabled="disabled" @submit.prevent="() => {}">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200px" flat color="transparent">
          <v-card-title>
            <FormHeader
              :loading="loading"
              :disabled="disabled"
              :link="'page-guides'"
              :title="getTitle ? `${getTitle} гид` : `Создать гида`"
            />
          </v-card-title>
          <v-card-text>
            <v-container>
              <PageContent height="85">
                <InputTitleSlug
                  :is-loading="loading || disabled"
                  :title="fields.name"
                  :title-error="validationErrors.name"
                  :slug="fields.slug"
                  :slug-error="validationErrors.slug"
                  :isSlugEnabled="!isNew"
                  @change:title="fields.name = $event"
                  @change:slug="fields.slug = $event"
                  @input:slug="$v.fields.slug.$touch"
                />
                <v-row>
                  <v-col cols="12">
                    <span>Описание * (<TranslationIcon />)</span>
                    <v-textarea
                      id="description"
                      outlined
                      :disabled="disabled"
                      :loading="loading"
                      v-model="fields.description"
                      :error-messages="validationErrors.description"
                      @onChangeContent="$emit('change:descriptionForMobile', $event)"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <span>Краткое описание * (<TranslationIcon />)</span>
                    <v-text-field
                      id="shortDescription"
                      outlined
                      :disabled="disabled"
                      :loading="loading"
                      v-model="fields.shortDescription"
                      :error-messages="validationErrors.shortDescription"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <MediaFilePicker
                      :multiple="false"
                      :file-type="$options.mediaTypesByKeys.IMAGE"
                      :value="fields.imageId"
                      @change="fields.imageId = $event"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="!!routes">
                    <span>Маршруты</span>
                    <v-select
                      v-model="fields.routes"
                      multiple
                      outlined
                      item-value="id"
                      item-text="title"
                      :items="routes"
                      :disabled="disabled"
                      :loading="loading"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="!!audioguides">
                    <span>Аудиогиды</span>
                    <v-select
                      v-model="fields.audioguides"
                      multiple
                      outlined
                      item-value="id"
                      item-text="title"
                      :items="audioguides"
                      :disabled="disabled"
                      :loading="loading"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="!!cities">
                    <span>Города</span>
                    <v-select
                      outlined
                      item-value="id"
                      item-text="title"
                      :items="cities"
                      :disabled="disabled"
                      :loading="loading"
                      v-model="fields.cityId"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <SeoSettings
                      :data="data"
                      :loading="loading"
                      :disabled="disabled"
                      @on-input-seo="fields = Object.assign({}, fields, $event)"
                    />
                  </v-col>
                </v-row>
              </PageContent>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          v-if="isNew"
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="isNew"
          :is-loading="loading"
          :is-submit-disabled="disabled"
          :language="language"
          :translations="fields.translations"
          :entity-id="data.id"
          :entity-type="'guide'"
          :sortOrder="fields.sortOrder"
          @cancel="$router.push({ name: 'page-guides' })"
          @submit="submit"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @update-sort-order="fields.sortOrder = Number($event)"
        >
          <template #info-append>
            <StatusPicker
              align="center"
              cols="12"
              title="Статус:"
              :disabled="disabled"
              :loading="loading"
              :status="fields.status"
              @status:change="fields.status = $event"
            />
          </template>
        </ManageSidePanel>
        <ManageSidePanel
          v-else-if="data"
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="!data.id"
          :created-at="data.createdAt"
          :updated-at="data.updatedAt"
          :is-loading="loading"
          :language="language"
          :translations="fields.translations"
          :entity-id="data.id"
          :entity-type="'guide'"
          :sortOrder="fields.sortOrder"
          @delete="$emit('delete-guide')"
          @cancel="$router.push({ name: 'page-guides' })"
          @submit="submit"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @update-sort-order="fields.sortOrder = Number($event)"
        >
          <template #info-append>
            <StatusPicker
              align="center"
              cols="12"
              title="Статус:"
              :disabled="disabled"
              :loading="loading"
              :status="fields.status"
              @status:change="fields.status = $event"
            />
            <v-row align="center">
              <v-col cols="6">
                <span>Посмотреть на сайте</span>
              </v-col>
              <v-col cols="6">
                <v-btn
                  icon
                  link
                  color="primary"
                  target="_blank"
                  :href="redirectToWebPortal"
                  :loading="loading"
                  :disabled="disabled"
                  ><v-icon>mdi-web</v-icon></v-btn
                >
              </v-col>
            </v-row>
            <v-row v-if="!!fields.detailsEntityId" align="center">
              <v-col cols="6">
                <span>Посмотреть общий доступ</span>
              </v-col>
              <v-col cols="6">
                <v-btn
                  icon
                  link
                  color="primary"
                  :to="redirectToSharedAccess"
                  :loading="loading"
                  :disabled="disabled"
                  ><v-icon>mdi-account-group</v-icon></v-btn
                >
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <DetailComponent
                  :loading="loading"
                  :disabled="disabled"
                  :details-entity-id="fields.detailsEntityId"
                  :details-entity-type="fields.detailsEntityType"
                  @change:details-entity-type="changeDetailsEntityType($event)"
                  @change:details-entity-id="fields.detailsEntityId = $event"
                />
              </v-col>
            </v-row>
          </template>
          <template #body>
            <v-card v-if="getEvents?.length || getAudioguides?.length || getRoutes?.length" flat>
              <v-card-subtitle class="grey--text">Связи</v-card-subtitle>
              <v-card-text>
                <v-row>
                  <RelationComponent
                    :events="getEvents"
                    :audioguides="getAudioguides"
                    :routes="getRoutes"
                    :shared-access="sharedAccess"
                    :ticket-reservations="ticketReservations"
                    :name="getTitle"
                    :loading="loading"
                    :disabled="disabled"
                  />
                </v-row>
              </v-card-text>
            </v-card>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>
<script>
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'
import { mixForm } from '@/utils/form.mix.js'

import validationRules from './validation-rules.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'

import FormAbstract from '@/components/abstract/form'
import ManageSidePanel from '@/components/manage-side-panel'
import MediaFilePicker from '@/components/media-file-picker'
import SeoSettings from '@/components/seo-settings'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import FormHeader from '@/components/form-header'
import InputTitleSlug from '@/components/inputs/InputTitleSlug'
import StatusPicker from '@/components/status-picker'
import PageContent from '@/components/page-content/PageContent.vue'
import ProgressCircular from '@/components/progress-circular'
import RelationComponent from '@/components/relation-component'
import DetailComponent from '@/components/detail-component'

export default {
  extends: FormAbstract,
  mixins: [validationTranslations, prepareTranslations, mixForm],
  mediaTypesByKeys: mediaTypesByKeys(),
  name: 'FormGuide',
  validationRules,
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    routes: {
      type: Array,
      default: null
    },
    audioguides: {
      type: Array,
      default: null
    },
    cities: {
      type: Array,
      default: null
    },
    sharedAccess: {
      type: Array,
      default: null
    },
    ticketReservations: {
      type: Array,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    language: {
      type: String,
      default: null
    }
  },
  components: {
    PageContent,
    InputTitleSlug,
    TranslationIcon,
    ManageSidePanel,
    MediaFilePicker,
    SeoSettings,
    FormHeader,
    StatusPicker,
    ProgressCircular,
    RelationComponent,
    DetailComponent
  },
  data () {
    return {
      fields: {
        name: null,
        slug: undefined,
        description: null,
        descriptionForMobile: null,
        shortDescription: null,
        imageId: null,
        routes: null,
        audioguides: null,
        cityId: null,
        seoMetaTitle: null,
        seoMetaDescription: null,
        seoDisallowIndex: false,
        seoMetaKeywords: null,
        seoRelCanonical: null,
        seoOgImage: null,
        status: null,
        detailsEntityId: null,
        detailsEntityType: null,
        translations: { ru: { isEnabled: true } },
        sortOrder: null
      }
    }
  },
  computed: {
    redirectToWebPortal () {
      if (this.data) {
        const { slug } = this.data
        return window.location.href.includes('demo')
          ? `${process.env.VUE_APP_WEB_PORTAL_URL_DEMO}/guides/${slug}`
          : `${process.env.VUE_APP_WEB_PORTAL_URL}/guides/${slug}`
      }
      return '#'
    },
    getTitle () {
      if (this.data) {
        return this.data?.name || ''
      }
      return null
    },
    validationRulesVars () {
      return { id: this.data?.id, ctx: this, isNew: this.isNew }
    },
    getAudioguides () {
      return this.data?.audioguides.map(audioguide => ({
        ...audioguide,
        __typename: 'Audioguide'
      }))
    },
    getEvents () {
      return this.data?.events || null
    },
    getRoutes () {
      return this.data?.routes.map(route => ({
        ...route,
        __typename: 'Route'
      }))
    },
    getId () {
      return this.data?.id || null
    }
  },
  watch: {
    data (val) {
      this.populate(val)
    }
  },
  methods: {
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }

      this.preparedField('audioguides')
      this.preparedField('routes')

      const payload = {
        ...this.composeFormData(this.fields)
      }

      if (!this.fields.detailsEntityId) {
        delete payload.detailsEntityId
        delete payload.detailsEntityType
      }
      delete payload.seoOgImage
      this.mixPrepareTranslations(payload)
      this.$emit('submit', payload)
    },
    preparedField (field) {
      if (!this.fields[field]) {
        return
      }
      this.fields[field] = this.fields[field].map(el => el.id)
    },
    changeDetailsEntityType (event) {
      this.fields.detailsEntityType = event
      this.fields.detailsEntityId = null
    }
  }
}
</script>
